import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MdDownload,
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import examAPI from '../../api/examAPI';
import admissionsAPI from '../../api/admissionsAPI';
import OdooAutocomplete from '../admissions/OdooAutocomplete';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import universityLogo from '../../../assets/images/Benadir University Logo.png';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const CAMPUS_FALLBACK = [
  { id: 'prof_addow', name: 'Prof Addow Campus' },
  { id: 'prof_shahiid', name: 'Prof Shahiid Campus' },
  { id: 'prof_shurie', name: 'Prof Shurie Campus' },
];

const SEMESTER_FALLBACK = Array.from({ length: 12 }, (_, i) => ({
  id: String(i + 1),
  value: String(i + 1),
  name: `Semester ${['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'][i]}`,
  label: `Semester ${['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'][i]}`,
}));

function Field({ label, children }) {
  return (
    <div className="ea-odoo-field">
      <label className="ea-odoo-field__label">{label}</label>
      <div className="ea-odoo-field__value">{children}</div>
    </div>
  );
}

function Group({ title, children, wide }) {
  return (
    <section className={`ea-odoo-group${wide ? ' ea-odoo-group--wide' : ''}`}>
      <h3 className="ea-odoo-group__title">{title}</h3>
      <div className="ea-odoo-group__body">{children}</div>
    </section>
  );
}

function withSelected(options, id, name) {
  if (!id) return options || [];
  if ((options || []).some((opt) => String(opt.id ?? opt.value) === String(id))) {
    return options;
  }
  return [{ id, name: name || '' }, ...(options || [])];
}

function formatSheetDate(d = new Date()) {
  return d
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .replace(/\s/g, '-');
}

function mapStudents(rows) {
  return (rows || []).map((row, idx) => ({
    id: row.id || row.student_id || `row-${idx}`,
    serial_no: row.serial_no || idx + 1,
    student_no: row.student_no || row.roll_number || '',
    student_name: row.student_name || row.name || '',
  }));
}

async function loadLogoImage() {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = universityLogo;
  await new Promise((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Logo failed to load'));
    setTimeout(() => reject(new Error('Logo load timeout')), 3000);
  });
  return img;
}

export default function TeacherSheetMarksPage() {
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const exportMenuRef = useRef(null);

  const [facultyId, setFacultyId] = useState('');
  const [facultyName, setFacultyName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [semester, setSemester] = useState('1');
  const [batchId, setBatchId] = useState('');
  const [batchName, setBatchName] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [subjectName, setSubjectName] = useState('');
  const [campus, setCampus] = useState('');
  const [campusName, setCampusName] = useState('');

  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [semesters, setSemesters] = useState(SEMESTER_FALLBACK);
  const [campuses, setCampuses] = useState(CAMPUS_FALLBACK);

  const [students, setStudents] = useState([]);
  const [shown, setShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [sheetDate] = useState(() => formatSheetDate());

  const loadLookups = useCallback(async () => {
    setLookupsLoading(true);
    const facRes = await fetchWithHrToken(ensureHrToken, (token) => examAPI.getFaculties(token));
    if (facRes.error === 'no_token') {
      setError(HR_SESSION_ERROR);
      setLookupsLoading(false);
      return;
    }
    if (facRes.error) setError(facRes.error);
    else setFaculties(facRes.data?.faculties || facRes.data?.records || []);

    const semRes = await fetchWithHrToken(ensureHrToken, (token) => examAPI.getSemesters(token));
    if (!semRes.error) {
      const list = semRes.data?.semesters || semRes.data?.records || [];
      if (list.length) setSemesters(list);
    }

    const campusRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getCampuses(token)
    );
    if (!campusRes.error) {
      const list = campusRes.data?.campuses || campusRes.data?.records || [];
      if (list.length) setCampuses(list);
    }
    setLookupsLoading(false);
  }, [ensureHrToken]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!facultyId) {
      setDepartments([]);
      return;
    }
    fetchWithHrToken(ensureHrToken, (token) => examAPI.getDepartments(facultyId, token)).then(
      ({ data }) => setDepartments(data?.departments || data?.records || [])
    );
  }, [facultyId, ensureHrToken]);

  useEffect(() => {
    if (!departmentId) {
      setBatches([]);
      return;
    }
    fetchWithHrToken(ensureHrToken, (token) => examAPI.getBatches(departmentId, token)).then(
      ({ data }) => setBatches(data?.batches || data?.records || [])
    );
  }, [departmentId, ensureHrToken]);

  useEffect(() => {
    if (!departmentId) {
      setSubjects([]);
      return;
    }
    fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.getSubjects(
        {
          faculty_id: facultyId ? Number(facultyId) : undefined,
          department_id: Number(departmentId),
          semester: semester || undefined,
          batch_id: batchId ? Number(batchId) : undefined,
        },
        token
      )
    ).then(({ data }) => setSubjects(data?.subjects || data?.records || []));
  }, [facultyId, departmentId, semester, batchId, ensureHrToken]);

  useEffect(() => {
    const onDoc = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const loadStudents = useCallback(async () => {
    if (!facultyId || !departmentId || !batchId) {
      setStudents([]);
      setShown(false);
      return;
    }

    setLoading(true);
    setError(null);
    setExportOpen(false);

    const filters = {
      faculty_id: Number(facultyId),
      department_id: Number(departmentId),
      batch_id: Number(batchId),
      semester: semester ? String(semester) : undefined,
      subject_id: subjectId ? Number(subjectId) : undefined,
      campus: campus || undefined,
      limit: 5000,
    };

    let rows = [];

    if (subjectId) {
      const preview = await fetchWithHrToken(ensureHrToken, (token) =>
        examAPI.previewStudents(
          {
            batch_id: Number(batchId),
            faculty_id: Number(facultyId),
            department_id: Number(departmentId),
            semester: String(semester || '1'),
            subject_id: Number(subjectId),
            campus: campus || undefined,
          },
          token
        )
      );
      if (preview.error === 'no_token') {
        setError(HR_SESSION_ERROR);
        setLoading(false);
        return;
      }
      if (!preview.error) rows = preview.data?.students || preview.data?.records || [];
    }

    if (!rows.length) {
      const listRes = await fetchWithHrToken(ensureHrToken, (token) =>
        examAPI.getMarksStudentList(filters, token)
      );
      if (listRes.error === 'no_token') {
        setError(HR_SESSION_ERROR);
        setLoading(false);
        return;
      }
      if (listRes.error) {
        setError(listRes.error);
        setStudents([]);
        setShown(false);
        setLoading(false);
        return;
      }
      rows = listRes.data?.students || listRes.data?.records || [];
    }

    if (!rows.length) {
      setError('No students found for the selected filters.');
      setStudents([]);
      setShown(false);
      setLoading(false);
      return;
    }

    setStudents(mapStudents(rows));
    setShown(true);
    setLoading(false);
  }, [
    facultyId,
    departmentId,
    batchId,
    semester,
    subjectId,
    campus,
    ensureHrToken,
  ]);

  useEffect(() => {
    if (!facultyId || !departmentId || !batchId) {
      setStudents([]);
      setShown(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      loadStudents();
    }, 200);
    return () => clearTimeout(timer);
  }, [facultyId, departmentId, batchId, semester, subjectId, campus, loadStudents]);

  const exportPdf = async () => {
    if (!students.length) {
      setError('Load students first, then export.');
      return;
    }
    setExportOpen(false);

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.width;
    const logoSize = 24;
    const logoTextGap = 8;
    const textWidth = 80;
    const groupStartX = (pageWidth - (logoSize + logoTextGap + textWidth)) / 2;
    const logoX = groupStartX;
    const logoY = 10;
    const textStartX = groupStartX + logoSize + logoTextGap;

    let logoImg = null;
    try {
      logoImg = await loadLogoImage();
    } catch {
      logoImg = null;
    }

    const metaY = 40;
    const leftColX = 14;
    const rightColX = 105;
    const rowHeight = 8;
    const tableWidth = 182;
    const metaHeight = rowHeight * 3;

    const drawHeader = (pageDoc) => {
      if (logoImg) {
        try {
          pageDoc.addImage(logoImg, 'PNG', logoX, logoY, logoSize, logoSize, 'university-logo', 'FAST');
        } catch {
          /* keep going without logo */
        }
      }

      pageDoc.setTextColor(0, 0, 0);
      pageDoc.setFontSize(18);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('BENADIR UNIVERSITY', textStartX, 15);

      pageDoc.setFontSize(9);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text('CULTIVATING HUMAN TALENTS', textStartX, 22);

      pageDoc.setFontSize(14);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Exam Result Sheet', textStartX, 30);

      pageDoc.setDrawColor(0, 0, 0);
      pageDoc.setLineWidth(0.5);
      pageDoc.rect(leftColX, metaY, tableWidth, metaHeight);
      pageDoc.line(leftColX, metaY + rowHeight, leftColX + tableWidth, metaY + rowHeight);
      pageDoc.line(leftColX, metaY + rowHeight * 2, leftColX + tableWidth, metaY + rowHeight * 2);
      pageDoc.line(leftColX + tableWidth / 2, metaY, leftColX + tableWidth / 2, metaY + metaHeight);

      pageDoc.setFontSize(10);
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Faculty:', leftColX + 2, metaY + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(facultyName || '—', leftColX + 28, metaY + 5.5);

      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Department:', leftColX + 2, metaY + rowHeight + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(departmentName || '—', leftColX + 34, metaY + rowHeight + 5.5);

      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Batch:', leftColX + 2, metaY + rowHeight * 2 + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(batchName || '—', leftColX + 24, metaY + rowHeight * 2 + 5.5);

      const rightX = rightColX + 4;
      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Subject:', rightX, metaY + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(subjectName || '', rightX + 26, metaY + 5.5);

      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Lecturer Name:', rightX, metaY + rowHeight + 5.5);
      pageDoc.setLineWidth(0.3);
      pageDoc.line(rightX + 36, metaY + rowHeight + 6, rightX + 36 + 48, metaY + rowHeight + 6);

      pageDoc.setFont('helvetica', 'bold');
      pageDoc.text('Date:', rightX, metaY + rowHeight * 2 + 5.5);
      pageDoc.setFont('helvetica', 'normal');
      pageDoc.text(sheetDate, rightX + 18, metaY + rowHeight * 2 + 5.5);
    };

    drawHeader(doc);

    autoTable(doc, {
      startY: metaY + metaHeight + 6,
      head: [['S#', 'Student ID', 'Student Name', 'Midterm', 'Final', 'Oral', 'Total']],
      body: students.map((s, idx) => [
        s.serial_no || idx + 1,
        s.student_no || '',
        s.student_name || '',
        '',
        '',
        '',
        '',
      ]),
      theme: 'grid',
      showHead: 'everyPage',
      styles: {
        fontSize: 9,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 10,
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 28 },
        2: { cellWidth: 70 },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 18, halign: 'center' },
        6: { cellWidth: 18, halign: 'center' },
      },
      // Page 1 keeps room for the university header above; later pages are table-only.
      margin: { top: 14, left: 14, right: 14, bottom: 16 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 8,
        { align: 'center' }
      );
    }

    doc.save(
      `Exam_Result_Sheet_${batchName || 'Batch'}_${subjectName || 'Subject'}.pdf`.replace(
        /\s+/g,
        '_'
      )
    );
  };

  const exportExcel = () => {
    if (!students.length) {
      setError('Load students first, then export.');
      return;
    }
    setExportOpen(false);

    const meta = [
      ['BENADIR UNIVERSITY'],
      ['CULTIVATING HUMAN TALENTS'],
      ['Exam Result Sheet'],
      [],
      ['Faculty', facultyName || ''],
      ['Department', departmentName || ''],
      ['Batch', batchName || ''],
      ['Subject', subjectName || ''],
      ['Campus', campusName || ''],
      ['Lecturer Name', ''],
      ['Date', sheetDate],
      [],
    ];
    const rows = students.map((s, idx) => ({
      'S#': s.serial_no || idx + 1,
      'Student ID': s.student_no || '',
      'Student Name': s.student_name || '',
      Midterm: '',
      Final: '',
      Oral: '',
      Total: '',
    }));

    const wb = XLSX.utils.book_new();
    const wsMeta = XLSX.utils.aoa_to_sheet(meta);
    const wsStudents = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, wsMeta, 'Header');
    XLSX.utils.book_append_sheet(wb, wsStudents, 'Exam Result Sheet');
    XLSX.writeFile(
      wb,
      `Exam_Result_Sheet_${batchName || 'Batch'}_${subjectName || 'Subject'}.xlsx`.replace(
        /\s+/g,
        '_'
      )
    );
  };

  const facultyOptions = withSelected(faculties, facultyId, facultyName);
  const departmentOptions = withSelected(departments, departmentId, departmentName);
  const batchOptions = withSelected(batches, batchId, batchName);
  const subjectOptions = withSelected(
    subjects.map((s) => ({ id: s.id, name: s.name })),
    subjectId,
    subjectName
  );
  const campusOptions = withSelected(
    campuses.map((c) => ({ id: c.id ?? c.value, name: c.name || c.label })),
    campus,
    campusName
  );

  return (
    <div className="ea-odoo-form-page ea-adm-form ea-exam-form">
      <div className="ea-exam-form-header">
        <div className="ea-exam-export-wrap" ref={exportMenuRef}>
          <button
            type="button"
            className="ea-odoo-btn ea-odoo-btn--primary"
            onClick={() => setExportOpen((o) => !o)}
            disabled={!shown || students.length === 0}
          >
            <MdDownload size={16} aria-hidden />
            Export Report
          </button>
          {exportOpen ? (
            <div className="ea-exam-export-menu" role="menu">
              <button type="button" role="menuitem" onClick={exportPdf}>
                Download PDF
              </button>
              <button type="button" role="menuitem" onClick={exportExcel}>
                Download Excel
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {error && (
        <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
          {error}
        </div>
      )}

      <div className="ea-odoo-sheet ea-exam-sheet">
        <div className="ea-odoo-sheet__body ea-exam-sheet__body">
          {lookupsLoading ? (
            <p className="ea-hr-table__empty">Loading…</p>
          ) : (
            <>
              <div className="ea-odoo-cols">
                <Group title="Academic Information">
                  <Field label="Faculty">
                    <OdooAutocomplete
                      value={facultyId}
                      options={facultyOptions}
                      placeholder=""
                      onChange={(val) => {
                        const found = facultyOptions.find((f) => String(f.id) === String(val));
                        setFacultyId(val);
                        setFacultyName(found?.name || '');
                        setDepartmentId('');
                        setDepartmentName('');
                        setBatchId('');
                        setBatchName('');
                        setSubjectId('');
                        setSubjectName('');
                      }}
                    />
                  </Field>
                  <Field label="Department">
                    <OdooAutocomplete
                      value={departmentId}
                      options={departmentOptions}
                      disabled={!facultyId}
                      placeholder=""
                      onChange={(val) => {
                        const found = departmentOptions.find((d) => String(d.id) === String(val));
                        setDepartmentId(val);
                        setDepartmentName(found?.name || '');
                        setBatchId('');
                        setBatchName('');
                        setSubjectId('');
                        setSubjectName('');
                      }}
                    />
                  </Field>
                  <Field label="Semester">
                    <OdooAutocomplete
                      value={semester}
                      options={semesters}
                      placeholder=""
                      onChange={(val) => {
                        setSemester(val || '1');
                        setSubjectId('');
                        setSubjectName('');
                      }}
                    />
                  </Field>
                </Group>

                <Group title="Exam Information">
                  <Field label="Batch">
                    <OdooAutocomplete
                      value={batchId}
                      options={batchOptions}
                      disabled={!departmentId}
                      placeholder=""
                      onChange={(val) => {
                        const found = batchOptions.find((b) => String(b.id) === String(val));
                        setBatchId(val);
                        setBatchName(found?.name || '');
                        setSubjectId('');
                        setSubjectName('');
                      }}
                    />
                  </Field>
                  <Field label="Subject">
                    <OdooAutocomplete
                      value={subjectId}
                      options={subjectOptions}
                      disabled={!departmentId}
                      placeholder=""
                      onChange={(val) => {
                        const found = subjectOptions.find((s) => String(s.id) === String(val));
                        setSubjectId(val);
                        setSubjectName(found?.name || '');
                      }}
                    />
                  </Field>
                </Group>
              </div>

              <Group title="Additional Filters" wide>
                <Field label="Campus">
                  <OdooAutocomplete
                    value={campus}
                    options={campusOptions}
                    placeholder=""
                    onChange={(val) => {
                      const found = campusOptions.find(
                        (c) => String(c.id ?? c.value) === String(val)
                      );
                      setCampus(val);
                      setCampusName(found?.name || found?.label || '');
                    }}
                  />
                </Field>
              </Group>

              {loading ? (
                <p className="ea-hr-table__empty" style={{ padding: '8px 0' }}>
                  Loading students…
                </p>
              ) : null}
            </>
          )}
        </div>

        {shown ? (
          <div className="ea-exam-result-sheet">
            <div className="ea-exam-result-sheet__header">
              <img
                src={universityLogo}
                alt="Benadir University"
                className="ea-exam-result-sheet__logo"
              />
              <div className="ea-exam-result-sheet__brand">
                <h1>BENADIR UNIVERSITY</h1>
                <p>CULTIVATING HUMAN TALENTS</p>
                <h2>Exam Result Sheet</h2>
              </div>
            </div>

            <table className="ea-exam-result-sheet__meta">
              <tbody>
                <tr>
                  <td>
                    <strong>Faculty:</strong> {facultyName || '—'}
                  </td>
                  <td>
                    <strong>Subject:</strong> {subjectName || ''}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Department:</strong> {departmentName || '—'}
                  </td>
                  <td>
                    <strong>Lecturer Name:</strong>{' '}
                    <span className="ea-exam-result-sheet__line">________________</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Batch:</strong> {batchName || '—'}
                  </td>
                  <td>
                    <strong>Date:</strong> {sheetDate}
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="ea-exam-result-sheet__table">
              <thead>
                <tr>
                  <th>S#</th>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Midterm</th>
                  <th>Final</th>
                  <th>Oral</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ea-hr-table__empty">
                      No students found.
                    </td>
                  </tr>
                ) : (
                  students.map((s, idx) => (
                    <tr key={s.id}>
                      <td className="ea-exam-result-sheet__sn">{s.serial_no || idx + 1}</td>
                      <td>{s.student_no}</td>
                      <td className="ea-exam-result-sheet__name">{s.student_name}</td>
                      <td />
                      <td />
                      <td />
                      <td />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="ea-exam-notebook__body">
            <p className="ea-hr-table__empty" style={{ padding: '24px 16px' }}>
              Select Faculty, Department and Batch — students load automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
